import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

// hooks
import useAuth from "hooks/useAuth";
import useTransactionsTable from "./hooks/useTransactionsTable";

// reusable components
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import TransactionSearchControls from "./components/TransactionSearchControls";
import TransactionsSummary from "./components/TransactionsSummary";

// utils
import { getFallbackStateTransactions } from "./utils/getFallbackStateTransactions";

const Transactions = () => {
  const {
    allTransactions,
    transactions: filteredTransactions,
    searchTerm,
    updateSearchTerm,
    typeFilter,
    updateTypeFilter,
    statusFilter,
    updateStatusFilter,
    transactionColumns,
    renderTransactionRow,
    isError,
    isLoading,
    refetch,
  } = useTransactionsTable();

  const { isAuthenticated } = useAuth();

  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timeout = setTimeout(() => updateSearchTerm(rawSearchTerm), 600);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  const fallback = getFallbackStateTransactions({
    isLoading,
    allTransactions,
    filteredTransactions,
  });

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyStateCard
          icon={
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full">
              <span className="text-red-600 font-bold text-lg">!</span>
            </div>
          }
          title="Something went wrong"
          description="We couldn't load your transactions. Please try again later."
          buttonLabel="Retry"
          onAction={refetch}
        />
      </div>
    );
  }

  return (
    <div className="pt-[2em]">
      <TransactionsSummary transactions={allTransactions} />

      <TransactionSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        typeFilter={typeFilter}
        updateTypeFilter={updateTypeFilter}
        statusFilter={statusFilter}
        updateStatusFilter={updateStatusFilter}
      />

      <SecureTableShell
        data={filteredTransactions}
        columns={transactionColumns}
        renderRow={renderTransactionRow}
        fallbackState={fallback.state}
        fallbackMessages={{
          noData: {
            title: "No transactions yet",
            description: "Completed sales and refunds will show up here.",
            buttonLabel: null,
          },
          noMatch: {
            title: "No matching transactions",
            description: "Try adjusting your search or filters.",
            buttonLabel: null,
          },
        }}
      />
    </div>
  );
};

export default Transactions;
